import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CalendarClock, CheckCircle2 } from "lucide-react";
import { fmtDate, fmtTime } from "./Conferences";

function ConferenceCard({ c, reload }) {
    const kids = c.eligible_students || [];
    const [kid, setKid] = React.useState(kids[0]?.id || "");
    const [busy, setBusy] = React.useState(false);
    const mine = c.my_booking;

    async function book(start) {
        if (!window.confirm(`Book ${fmtTime(start)} on ${fmtDate(c.date)} with ${c.teacher_name}?`)) return;
        setBusy(true);
        try {
            await api.post(`/conferences/${c.id}/book`, { slot_start: start, student_id: kid || null });
            toast.success("Booked! A confirmation email is on its way.");
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); reload(); }
        finally { setBusy(false); }
    }
    async function cancel() {
        if (!window.confirm("Cancel your conference time?")) return;
        await api.delete(`/conference-bookings/${mine.id}`);
        toast.success("Booking cancelled");
        reload();
    }
    return (
        <Card data-testid={`parent-conference-${c.id}`}>
            <CardContent className="p-5">
                <div className="font-medium">{c.teacher_name} · {c.class_name}</div>
                {kids.length > 0 && <div className="text-xs text-primary font-medium" data-testid={`conference-for-${c.id}`}>For {kids.map(k => k.first_name).join(", ")}</div>}
                <div className="text-xs text-brand-stone mb-2">{fmtDate(c.date)} · {fmtTime(c.start_time)}–{fmtTime(c.end_time)} · {c.slot_minutes} min each{c.location ? ` · ${c.location}` : ""}</div>
                {c.note && <p className="text-sm mb-3 whitespace-pre-wrap">{c.note}</p>}
                {mine ? (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`my-booking-${c.id}`}>
                        <span className="text-sm text-emerald-800 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> You're booked for <b>{fmtTime(mine.slot_start)}</b>{mine.student_name ? ` (${mine.student_name})` : ""}</span>
                        <Button variant="outline" size="sm" onClick={cancel} data-testid={`cancel-booking-${c.id}`}>Cancel</Button>
                    </div>
                ) : (
                    <>
                        {kids.length > 1 && (
                            <div className="mb-3 max-w-xs">
                                <Select value={kid} onValueChange={setKid}>
                                    <SelectTrigger className="h-8 text-xs" data-testid={`conference-kid-${c.id}`}><SelectValue placeholder="About which child?" /></SelectTrigger>
                                    <SelectContent>{kids.map(k => <SelectItem key={k.id} value={k.id}>{k.first_name} {k.last_name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                            {c.slots.map(s => (
                                <Button key={s.start} size="sm" variant="outline" disabled={s.taken || busy} onClick={() => book(s.start)}
                                    className={s.taken ? "line-through opacity-50" : "hover:bg-primary hover:text-white"} data-testid={`book-slot-${c.id}-${s.start}`}>
                                    {fmtTime(s.start)}
                                </Button>
                            ))}
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

/** Parent: see open conference times for their children's teachers and book one. */
export function ParentConferences() {
    const [items, setItems] = React.useState(null);
    const load = React.useCallback(() => { api.get("/conferences/available").then(r => setItems(r.data)); }, []);
    React.useEffect(() => { load(); }, [load]);
    return (
        <div data-testid="parent-conferences">
            <div className="flex items-center gap-2 mb-4"><CalendarClock className="w-4 h-4 text-primary" /><h2 className="font-dash text-xl font-semibold">Parent-teacher conferences</h2></div>
            <div className="space-y-3">
                {items?.map(c => <ConferenceCard key={c.id} c={c} reload={load} />)}
                {items && items.length === 0 && <Card><CardContent className="p-10 text-center text-brand-stone text-sm">No conference times are open right now. You'll get an email when a teacher opens them.</CardContent></Card>}
            </div>
        </div>
    );
}

/** Small banner for the parent dashboard when conference times are open. */
export function ConferenceNotice({ onOpen }) {
    const [n, setN] = React.useState(0);
    React.useEffect(() => {
        api.get("/conferences/available").then(r => setN(r.data.filter(c => !c.my_booking && c.status === "open").length)).catch(() => {});
    }, []);
    if (!n) return null;
    return (
        <button onClick={onOpen} className="w-full mb-6 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-left text-sm flex items-center gap-2 hover:bg-primary/10 transition-colors" data-testid="conference-notice">
            <CalendarClock className="w-4 h-4 text-primary" />
            <span><b>{n} teacher{n === 1 ? " has" : "s have"} opened conference times.</b> Tap to book yours.</span>
        </button>
    );
}
